import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  Undo2,
  Redo2,
  Monitor,
  Tablet,
  Smartphone,
  Eye,
  Rocket,
  Check,
  Loader2,
  AlertCircle,
  Circle,
  PanelLeft,
  PanelRight,
} from "lucide-react";
import { ZirroMark } from "./ZirroMark";
import { useEditorStore, type Viewport, type SaveStatus } from "../store";

export interface TopBarProps {
  onPreview?: () => void;
  onPublish?: () => void;
}

const VIEWPORTS: { id: Viewport; label: string; icon: typeof Monitor }[] = [
  { id: "desktop", label: "Desktop", icon: Monitor },
  { id: "tablet", label: "Tablet", icon: Tablet },
  { id: "mobile", label: "Mobile", icon: Smartphone },
];

function SaveBadge({ status }: { status: SaveStatus }) {
  const map: Record<SaveStatus, { label: string; cls: string; icon: typeof Check }> = {
    saved:   { label: "Saved", cls: "zirro-save-badge--saved", icon: Check },
    saving:  { label: "Saving", cls: "zirro-save-badge--saving", icon: Loader2 },
    unsaved: { label: "Unsaved", cls: "zirro-save-badge--unsaved", icon: Circle },
    error:   { label: "Not saved", cls: "zirro-save-badge--error", icon: AlertCircle },
  };
  const entry = map[status];

  if (status === "error") return <SaveErrorBadge label={entry.label} cls={entry.cls} />;

  return (
    <span className={`zirro-save-badge ${entry.cls}`} role="status">
      <span className="zirro-save-badge__dot" />
      {entry.label}
    </span>
  );
}

/** The failed-save badge: opens a panel saying what went wrong, with a retry. */
function SaveErrorBadge({ label, cls }: { label: string; cls: string }) {
  const saveError = useEditorStore((s) => s.saveError);
  const retrySave = useEditorStore((s) => s.retrySave);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const message =
    saveError ?? "Your changes couldn't be saved. They're kept in this browser — try again in a moment.";

  return (
    <div className="zirro-save-error" ref={ref}>
      <button
        type="button"
        className={`zirro-save-badge ${cls}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Changes not saved: ${message}`}
      >
        <span className="zirro-save-badge__dot" />
        {label}
      </button>

      {open ? (
        <div className="zirro-save-error__panel" role="dialog" aria-label="Save failed">
          <div className="zirro-save-error__title">
            <AlertCircle size={14} aria-hidden="true" />
            Changes not saved
          </div>
          <p className="zirro-save-error__message">{message}</p>
          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--primary"
            onClick={() => {
              setOpen(false);
              retrySave();
            }}
          >
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function TopBar({ onPreview, onPublish }: TopBarProps) {
  const viewport = useEditorStore((s) => s.viewport);
  const setViewport = useEditorStore((s) => s.setViewport);
  const mode = useEditorStore((s) => s.mode);
  const setMode = useEditorStore((s) => s.setMode);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const history = useEditorStore((s) => s.history);
  const future = useEditorStore((s) => s.future);
  const saveStatus = useEditorStore((s) => s.saveStatus);
  const leftSidebarCollapsed = useEditorStore((s) => s.leftSidebarCollapsed);
  const toggleLeftSidebar = useEditorStore((s) => s.toggleLeftSidebar);
  const rightSidebarCollapsed = useEditorStore((s) => s.rightSidebarCollapsed);
  const toggleRightSidebar = useEditorStore((s) => s.toggleRightSidebar);

  return (
    <div className="zirro-topbar">
      {/* Left */}
      <div className="zirro-topbar__left">
        <Link to="/app/dashboard" className="zirro-topbar__logo" title="Back to Dashboard">
          <ZirroMark size={20} className="zirro-topbar__logo-mark" />
          Zirro
        </Link>

        <div className="zirro-topbar__divider" />

        <button
          type="button"
          className={`zirro-ed-btn zirro-ed-btn--icon ${!leftSidebarCollapsed ? "zirro-ed-btn--active" : ""}`}
          onClick={toggleLeftSidebar}
          title={leftSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={leftSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <PanelLeft size={16} />
        </button>

        <button
          type="button"
          className="zirro-ed-btn zirro-ed-btn--icon"
          disabled={history.length === 0}
          onClick={undo}
          title="Undo (⌘Z)"
        >
          <Undo2 size={16} />
        </button>
        <button
          type="button"
          className="zirro-ed-btn zirro-ed-btn--icon"
          disabled={future.length === 0}
          onClick={redo}
          title="Redo (⌘⇧Z)"
        >
          <Redo2 size={16} />
        </button>
      </div>

      {/* Center — Viewport & Mode */}
      <div className="zirro-topbar__center">
        <div className="zirro-viewport-switch" role="group" aria-label="Mode toggle" style={{ marginRight: 8 }}>
          <button
            type="button"
            className={`zirro-ed-btn ${mode === "edit" ? "zirro-ed-btn--active" : ""}`}
            onClick={() => setMode("edit")}
            title="Edit Mode"
          >
            Edit
          </button>
          <button
            type="button"
            className={`zirro-ed-btn ${mode === "preview" ? "zirro-ed-btn--active" : ""}`}
            onClick={() => setMode("preview")}
            title="Preview Mode"
          >
            Live Preview
          </button>
        </div>

        <div className="zirro-viewport-switch" role="group" aria-label="Viewport">
          {VIEWPORTS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                type="button"
                className={`zirro-ed-btn ${viewport === v.id ? "zirro-ed-btn--active" : ""}`}
                aria-pressed={viewport === v.id}
                onClick={() => setViewport(v.id)}
                title={v.label}
              >
                <Icon size={15} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Right */}
      <div className="zirro-topbar__right">
        <SaveBadge status={saveStatus} />

        <button
          type="button"
          className={`zirro-ed-btn zirro-ed-btn--icon ${!rightSidebarCollapsed ? "zirro-ed-btn--active" : ""}`}
          onClick={toggleRightSidebar}
          title={rightSidebarCollapsed ? "Expand inspector" : "Collapse inspector"}
          aria-label={rightSidebarCollapsed ? "Expand inspector" : "Collapse inspector"}
        >
          <PanelRight size={16} />
        </button>

        {onPreview ? (
          <button type="button" className="zirro-ed-btn" onClick={onPreview} title="Full Screen Preview">
            <Eye size={15} />
            Full Preview
          </button>
        ) : null}

        {onPublish ? (
          <button type="button" className="zirro-ed-btn zirro-ed-btn--primary" onClick={onPublish} title="Publish site">
            <Rocket size={15} />
            Publish
          </button>
        ) : null}
      </div>
    </div>
  );
}
